import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Hand, Check, Clock, UserCheck, ShieldCheck, Search, Plus, Trash2, RefreshCw, History } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const PSTATUS = {
  waiting: { c: "bg-gray-100 text-gray-600", label: "Awaiting Pickup" },
  arrived: { c: "bg-amber-100 text-amber-800", label: "Parent Arrived" },
  released: { c: "bg-teal text-white", label: "Released" },
  staying: { c: "bg-indigo-100 text-indigo-700", label: "Staying for Next Class" },
  not_required_absent: { c: "bg-gray-200 text-gray-500", label: "Not Required—Absent" },
};
const effStatus = (row) => (row.attendance_status === "absent" && (row.status === "waiting" || row.status === "staying" || !row.status)) ? "not_required_absent" : row.status;
const ATT_LABEL = { present: "Present", late: "Late", absent: "Absent", excused: "Excused", observing: "Observing" };
const Badge = ({ s }) => { const st = PSTATUS[s] || PSTATUS.waiting; return <span className={`inline-block rounded-full text-xs px-2.5 py-1 font-semibold ${st.c}`} data-testid={`status-${s}`}>{st.label}</span>; };
const timeStr = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "";
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Pickup() {
  const { user } = useAuth();
  const role = user?.role;
  const isDirector = role === "director";
  const isStaff = isDirector || role === "teacher";
  const [tab, setTab] = useState(role === "parent" ? "mine" : "roster");

  return (
    <div>
      <PageHeader title="Dancer Pickup" subtitle="Safe, verified sign-out for every class." testid="pickup-page" />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white rounded-full p-1 mb-6 flex-wrap h-auto">
          {role === "parent" && <TabsTrigger value="mine" data-testid="pickup-tab-mine" className="rounded-full data-[state=active]:bg-teal data-[state=active]:text-white">I'm Here for Pickup</TabsTrigger>}
          {role === "parent" && <TabsTrigger value="adults" data-testid="pickup-tab-adults" className="rounded-full data-[state=active]:bg-teal data-[state=active]:text-white">Authorized Adults</TabsTrigger>}
          {isStaff && <TabsTrigger value="roster" data-testid="pickup-tab-roster" className="rounded-full data-[state=active]:bg-teal data-[state=active]:text-white">Pickup Roster</TabsTrigger>}
          {isDirector && <TabsTrigger value="board" data-testid="pickup-tab-board" className="rounded-full data-[state=active]:bg-teal data-[state=active]:text-white">Still Waiting</TabsTrigger>}
          {isStaff && <TabsTrigger value="history" data-testid="pickup-tab-history" className="rounded-full data-[state=active]:bg-teal data-[state=active]:text-white">History</TabsTrigger>}
          {isDirector && <TabsTrigger value="settings" data-testid="pickup-tab-settings" className="rounded-full data-[state=active]:bg-teal data-[state=active]:text-white">Settings</TabsTrigger>}
        </TabsList>
        {role === "parent" && <TabsContent value="mine"><ParentPickup /></TabsContent>}
        {role === "parent" && <TabsContent value="adults"><AuthorizedAdults /></TabsContent>}
        {isStaff && <TabsContent value="roster"><StaffRoster isDirector={isDirector} /></TabsContent>}
        {isDirector && <TabsContent value="board"><DirectorBoard /></TabsContent>}
        {isStaff && <TabsContent value="history"><PickupHistory isDirector={isDirector} /></TabsContent>}
        {isDirector && <TabsContent value="settings"><PickupSettings /></TabsContent>}
      </Tabs>
    </div>
  );
}

function usePoll(fn, deps = []) {
  useEffect(() => { fn(); const t = setInterval(fn, 10000); return () => clearInterval(t); /* eslint-disable-next-line */ }, deps);
}

/* ---------------- Parent ---------------- */
function ParentPickup() {
  const [items, setItems] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const load = useCallback(() => { api.get("/pickup/my-dancers").then((r) => setItems(r.data.items)).catch(() => {}); }, []);
  usePoll(load, []);
  const arrive = async (it) => {
    try { await api.post("/pickup/arrive", { class_id: it.class_id, dancer_id: it.dancer_id }); toast.success("Thanks! Please wait in the lobby."); setConfirm(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not check in"); setConfirm(null); }
  };
  const cancel = async (it) => {
    try { await api.post("/pickup/cancel-arrival", { class_id: it.class_id, dancer_id: it.dancer_id }); toast.success("Arrival cancelled"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  if (!items.length) return <EmptyState icon={Hand} title="No pickups right now" hint="Your dancer's pickup check-in opens 5 minutes before class ends." />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((it) => (
        <div key={`${it.class_id}-${it.dancer_id}`} data-testid={`pickup-card-${it.dancer_id}`} className="bg-white rounded-2xl border border-gray-100 p-5">
          <p className="font-head font-bold text-lg">{it.dancer_name}</p>
          <p className="text-sm text-muted-foreground">{it.class_name}{it.room ? ` · ${it.room}` : ""}</p>
          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Ends {it.end_time}</p>
          {it.next_class && <p className="text-xs text-indigo-700 mt-1">Has another class next: {it.next_class.class_name} at {it.next_class.start_time}</p>}
          <div className="mt-3"><Badge s={it.status} /></div>
          {it.status === "released" ? <p className="text-sm text-teal-dark mt-3 font-semibold flex items-center gap-1"><Check className="w-4 h-4" />Released by your teacher</p>
            : it.status === "arrived" ? (
              <div className="mt-3">
                <p className="text-sm text-muted-foreground">Please wait in the lobby. Your dancer will be released after the teacher confirms you're here.</p>
                {it.can_cancel && <Button variant="outline" size="sm" className="rounded-full mt-2" data-testid={`pickup-cancel-${it.dancer_id}`} onClick={() => cancel(it)}>I left / cancel arrival</Button>}
              </div>
            ) : (
              <Button data-testid={`pickup-arrive-${it.dancer_id}`} disabled={!it.window_open} onClick={() => setConfirm(it)}
                className="w-full rounded-full bg-teal hover:bg-teal-dark mt-3 h-12 text-base">
                <Hand className="w-5 h-5 mr-2" />{it.window_open ? "I'm Here for Pickup" : "Opens 5 min before class ends"}
              </Button>
            )}
        </div>
      ))}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader><DialogTitle className="font-head">Confirm pickup</DialogTitle></DialogHeader>
          <p className="text-sm">I'm physically in the lobby for pick up of <b>{confirm?.dancer_name}</b> from {confirm?.class_name}.</p>
          <p className="text-xs text-muted-foreground">Your dancer stays with the teacher until they confirm you're here and release them.</p>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button data-testid="pickup-confirm-arrive" className="rounded-full bg-teal hover:bg-teal-dark" onClick={() => arrive(confirm)}>Yes, I'm in the lobby</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AuthorizedAdults() {
  const [adults, setAdults] = useState([]);
  const load = () => api.get("/pickup/authorized-adults").then((r) => setAdults(r.data.adults || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (i, k, v) => setAdults((a) => a.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const add = () => setAdults((a) => [...a, { name: "", relationship: "", phone: "" }]);
  const remove = (i) => setAdults((a) => a.filter((_, j) => j !== i));
  const save = async () => {
    try { await api.put("/pickup/authorized-adults", { adults: adults.filter((a) => a.name.trim()) }); toast.success("Saved"); load(); }
    catch { toast.error("Error"); }
  };
  return (
    <div className="max-w-xl">
      <p className="text-sm text-muted-foreground mb-4">Adults you authorize to pick up your dancers. Your teachers and directors can see this list.</p>
      <div className="space-y-2">
        {adults.map((a, i) => (
          <div key={i} data-testid={`adult-row-${i}`} className="flex gap-2 items-center bg-white rounded-xl border p-2">
            <Input data-testid={`adult-name-${i}`} placeholder="Full name" value={a.name} onChange={(e) => set(i, "name", e.target.value)} />
            <Input data-testid={`adult-rel-${i}`} placeholder="Relationship" value={a.relationship} onChange={(e) => set(i, "relationship", e.target.value)} />
            <Input data-testid={`adult-phone-${i}`} placeholder="Phone" value={a.phone} onChange={(e) => set(i, "phone", e.target.value)} />
            <Button variant="outline" size="sm" className="rounded-full text-red-600 border-red-200" data-testid={`adult-remove-${i}`} onClick={() => remove(i)}><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <Button variant="outline" className="rounded-full" data-testid="adult-add" onClick={add}><Plus className="w-4 h-4 mr-1" />Add adult</Button>
        <Button className="rounded-full bg-teal hover:bg-teal-dark" data-testid="adult-save" onClick={save}>Save list</Button>
      </div>
    </div>
  );
}

/* ---------------- Staff roster ---------------- */
function StaffRoster({ isDirector }) {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [release, setRelease] = useState(null);
  const [receivedBy, setReceivedBy] = useState("");
  const [manual, setManual] = useState(null);
  const [manualName, setManualName] = useState("");
  useEffect(() => { api.get("/classes").then((r) => { setClasses(r.data); if (r.data[0] && !classId) setClassId(r.data[0].id); }).catch(() => {}); /* eslint-disable-next-line */ }, []);
  const load = useCallback(() => { if (classId && date) api.get("/pickup/roster", { params: { class_id: classId, date } }).then((r) => setData(r.data)).catch(() => setData(null)); }, [classId, date]);
  usePoll(load, [classId, date]);
  const doRelease = async () => {
    if (!receivedBy.trim()) { toast.error("Select the adult receiving the dancer"); return; }
    try { await api.post("/pickup/release", { class_id: classId, date, dancer_id: release.dancer_id, received_by: receivedBy }); toast.success(`${release.dancer_name} released`); setRelease(null); setReceivedBy(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const doManual = async () => {
    if (!manualName.trim()) { toast.error("Enter the adult's name"); return; }
    try { await api.post("/pickup/staff/arrive", { class_id: classId, date, dancer_id: manual.dancer_id, arrival_by: manualName }); toast.success("Arrival recorded"); setManual(null); setManualName(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const setStaying = async (row, staying) => {
    try { await api.post("/pickup/staying", { class_id: classId, date, dancer_id: row.dancer_id, staying }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4">
        <select data-testid="roster-class" className="border rounded-full h-10 px-4 text-sm bg-white" value={classId} onChange={(e) => setClassId(e.target.value)}>
          {classes.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.day} {c.end_time})</option>)}
        </select>
        <Input type="date" data-testid="roster-date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-full w-auto" />
        <Button variant="outline" className="rounded-full" data-testid="roster-refresh" onClick={load}><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
      </div>
      {!data ? <EmptyState icon={UserCheck} title="Pick a class and date" /> :
        !data.requires_pickup ? <EmptyState icon={ShieldCheck} title="Pickup not required for this class" hint="A director can enable it under Settings." /> :
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{data.class_name} · ends {data.end_time} · {data.date}</p>
            {data.roster.map((row) => { const eff = effStatus(row); const isAbsent = eff === "not_required_absent"; return (
              <div key={row.dancer_id} data-testid={`roster-row-${row.dancer_id}`} className={`bg-white rounded-2xl border p-4 ${row.attendance_discrepancy ? "border-red-300 ring-1 ring-red-200" : row.status === "arrived" ? "border-amber-300 ring-1 ring-amber-200" : "border-gray-100"}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-semibold">{row.dancer_name}</p>
                    <div className="mt-1 flex items-center gap-2 flex-wrap"><Badge s={eff} />
                      {row.attendance_status && <span data-testid={`att-chip-${row.dancer_id}`} className={`text-xs rounded-full px-2 py-0.5 font-semibold ${row.attendance_status === "absent" ? "bg-red-50 text-red-600" : row.attendance_status === "late" ? "bg-yellow-50 text-yellow-700" : "bg-teal/10 text-teal-dark"}`}>Attendance: {ATT_LABEL[row.attendance_status] || row.attendance_status}</span>}
                    </div>
                    {row.attendance_discrepancy && <p className="text-sm mt-1 text-red-700 font-semibold" data-testid={`discrepancy-${row.dancer_id}`}>⚠ Parent marked arrived but attendance shows Absent — please review before releasing.</p>}
                    {row.status === "arrived" && <p className="text-sm mt-1 text-amber-800">Here: <b>{row.arrival_by}</b> · {timeStr(row.arrival_time)}{row.arrival_manual ? " (recorded by staff)" : ""}</p>}
                    {row.status === "released" && <p className="text-sm mt-1 text-teal-dark">Released to <b>{row.received_by}</b> · {timeStr(row.release_time)} by {row.released_by_staff}</p>}
                    {isAbsent && <p className="text-xs text-muted-foreground mt-1">Marked absent — no pickup needed. If they arrive, change attendance to Present or Late.</p>}
                    {row.next_class && row.status !== "released" && <p className="text-xs text-indigo-700 mt-1">Back-to-back: {row.next_class.class_name} at {row.next_class.start_time}</p>}
                    {!!row.authorized_adults.length && <p className="text-xs text-muted-foreground mt-1">Authorized: {row.authorized_adults.map((a) => a.name).join(", ")}</p>}
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    {row.status !== "released" && !isAbsent && <Button size="sm" className="rounded-full bg-teal hover:bg-teal-dark" data-testid={`release-${row.dancer_id}`} onClick={() => { setRelease(row); setReceivedBy(row.arrival_by || (row.authorized_adults[0]?.name || "")); }}>Confirm Release</Button>}
                    {row.status === "waiting" && !isAbsent && <Button size="sm" variant="outline" className="rounded-full" data-testid={`manual-arrive-${row.dancer_id}`} onClick={() => { setManual(row); setManualName(row.authorized_adults[0]?.name || ""); }}>Mark arrived</Button>}
                    {row.status !== "released" && row.next_class && !isAbsent && <Button size="sm" variant="outline" className="rounded-full" data-testid={`staying-${row.dancer_id}`} onClick={() => setStaying(row, row.status !== "staying")}>{row.status === "staying" ? "Not staying" : "Staying next class"}</Button>}
                  </div>
                </div>
              </div>
            ); })}
          </div>}

      <Dialog open={!!release} onOpenChange={(o) => !o && setRelease(null)}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader><DialogTitle className="font-head">Confirm release — {release?.dancer_name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Verify the adult is physically in the lobby, then confirm who is receiving the dancer.</p>
          <Label className="mt-2">Receiving adult</Label>
          <select data-testid="release-received-by" className="w-full border rounded-md h-10 px-3" value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)}>
            <option value="">— select —</option>
            {(release?.authorized_adults || []).map((a) => <option key={a.name} value={a.name}>{a.name}{a.relationship ? ` (${a.relationship})` : ""}</option>)}
            {release?.arrival_by && !(release?.authorized_adults || []).some((a) => a.name === release.arrival_by) && <option value={release.arrival_by}>{release.arrival_by}</option>}
            <option value="__other">Other adult…</option>
          </select>
          {receivedBy === "__other" && <Input data-testid="release-other-name" placeholder="Adult's name" className="mt-2" onChange={(e) => setReceivedBy(e.target.value)} />}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setRelease(null)}>Cancel</Button>
            <Button data-testid="release-confirm" className="rounded-full bg-teal hover:bg-teal-dark" onClick={doRelease}>Confirm Release</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!manual} onOpenChange={(o) => !o && setManual(null)}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader><DialogTitle className="font-head">Record arrival — {manual?.dancer_name}</DialogTitle></DialogHeader>
          <Label>Adult who arrived</Label>
          <Input data-testid="manual-arrive-name" value={manualName} onChange={(e) => setManualName(e.target.value)} placeholder="Full name" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setManual(null)}>Cancel</Button>
            <Button data-testid="manual-arrive-confirm" className="rounded-full bg-teal hover:bg-teal-dark" onClick={doManual}>Record arrival</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Director board ---------------- */
function DirectorBoard() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/pickup/waiting").then((r) => setItems(r.data.items)).catch(() => {}), []);
  usePoll(load, []);
  if (!items.length) return <EmptyState icon={Check} title="Everyone's picked up" hint="No dancers are currently waiting." />;
  return (
    <div className="space-y-2">
      {items.map((it) => (
        <div key={`${it.class_id}-${it.dancer_id}`} data-testid={`waiting-${it.dancer_id}`} className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center justify-between gap-3 flex-wrap">
          <div><p className="font-semibold">{it.dancer_name}</p><p className="text-sm text-muted-foreground">{it.class_name} · ends {it.end_time}{it.arrival_by ? ` · ${it.arrival_by} here` : ""}</p></div>
          <Badge s={it.status} />
        </div>
      ))}
    </div>
  );
}

/* ---------------- History ---------------- */
function PickupHistory() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ date_from: "", date_to: "", q: "" });
  const [correct, setCorrect] = useState(null);
  const [note, setNote] = useState("");
  const load = () => api.get("/pickup/history", { params: { date_from: f.date_from || undefined, date_to: f.date_to || undefined } }).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f.date_from, f.date_to]);
  const shown = rows.filter((r) => !f.q || `${r.dancer_name} ${r.class_name} ${r.date} ${r.received_by || ""}`.toLowerCase().includes(f.q.toLowerCase()));
  const saveCorrect = async () => {
    try { await api.post("/pickup/correct", { record_id: correct.id, status: correct.status, received_by: correct.received_by, note }); toast.success("Correction saved"); setCorrect(null); setNote(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <Input type="date" data-testid="hist-from" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="rounded-full w-auto" />
        <Input type="date" data-testid="hist-to" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="rounded-full w-auto" />
        <div className="relative flex-1 min-w-[180px]"><Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" /><Input data-testid="hist-search" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search dancer, class, adult…" className="pl-9 rounded-full" /></div>
      </div>
      {!shown.length ? <EmptyState icon={History} title="No pickup records" /> :
        <div className="space-y-2">
          {shown.map((r) => (
            <div key={r.id} data-testid={`hist-${r.id}`} className="bg-white rounded-2xl border border-gray-100 p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-semibold">{r.dancer_name} <span className="text-sm text-muted-foreground">· {r.class_name} · {r.date}</span></p>
                  <div className="mt-1 flex items-center gap-2 flex-wrap"><Badge s={r.status} />
                    {r.attendance_status && <span className={`text-xs rounded-full px-2 py-0.5 font-semibold ${r.attendance_status === "absent" ? "bg-red-50 text-red-600" : r.attendance_status === "late" ? "bg-yellow-50 text-yellow-700" : "bg-teal/10 text-teal-dark"}`}>Attendance: {ATT_LABEL[r.attendance_status] || r.attendance_status}</span>}
                    {r.attendance_discrepancy && <span className="text-xs rounded-full px-2 py-0.5 font-semibold bg-red-100 text-red-700" data-testid={`hist-discrepancy-${r.id}`}>⚠ Attendance discrepancy</span>}
                  </div>
                  {r.arrival_by && <p className="text-sm text-muted-foreground mt-1">Parent arrived: <b>{r.arrival_by}</b> {timeStr(r.arrival_time)}{r.arrival_manual ? " (by staff)" : ""}</p>}
                  {r.status === "released" && <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1"><UserCheck className="w-3.5 h-3.5 text-teal" />Released by <b data-testid={`hist-releasedby-${r.id}`}>{r.released_by_staff || "Unknown"}</b></p>}
                  {!!(r.corrections || []).length && <p className="text-xs text-amber-700 mt-1">Corrected {r.corrections.length}× — latest by {r.corrections[r.corrections.length - 1].by_name}: {r.corrections[r.corrections.length - 1].note}</p>}
                </div>
                <Button size="sm" variant="outline" className="rounded-full" data-testid={`hist-correct-${r.id}`} onClick={() => { setCorrect({ id: r.id, status: r.status, received_by: r.received_by || "" }); setNote(""); }}>Correct</Button>
              </div>
            </div>
          ))}
        </div>}
      <Dialog open={!!correct} onOpenChange={(o) => !o && setCorrect(null)}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader><DialogTitle className="font-head">Correct record</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">The original entry is kept; your change and note are logged.</p>
          <Label>Status</Label>
          <select data-testid="correct-status" className="w-full border rounded-md h-10 px-3" value={correct?.status || ""} onChange={(e) => setCorrect({ ...correct, status: e.target.value })}>
            {Object.keys(PSTATUS).map((s) => <option key={s} value={s}>{PSTATUS[s].label}</option>)}
          </select>
          <Label className="mt-2">Receiving adult</Label>
          <Input data-testid="correct-received" value={correct?.received_by || ""} onChange={(e) => setCorrect({ ...correct, received_by: e.target.value })} />
          <Label className="mt-2">Reason for correction</Label>
          <Input data-testid="correct-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why are you changing this?" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setCorrect(null)}>Cancel</Button>
            <Button data-testid="correct-save" className="rounded-full bg-teal hover:bg-teal-dark" onClick={saveCorrect}>Save correction</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Settings ---------------- */
function PickupSettings() {
  const [cfg, setCfg] = useState(null);
  const load = () => api.get("/pickup/config").then((r) => setCfg(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = (key, id) => setCfg((c) => ({ ...c, [key]: c[key].includes(id) ? c[key].filter((x) => x !== id) : [...c[key], id] }));
  const save = async () => {
    try {
      await api.put("/studio/settings", { timezone: cfg.timezone, pickup_required_class_ids: cfg.pickup_required_class_ids, pickup_required_level_ids: cfg.pickup_required_level_ids });
      toast.success("Pickup settings saved");
    } catch { toast.error("Error"); }
  };
  if (!cfg) return null;
  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <Label>Studio timezone</Label>
        <select data-testid="pickup-timezone" className="w-full border rounded-md h-10 px-3 mt-1" value={cfg.timezone} onChange={(e) => setCfg({ ...cfg, timezone: e.target.value })}>
          {["America/Denver", "America/New_York", "America/Chicago", "America/Los_Angeles", "America/Phoenix", "America/Anchorage", "Pacific/Honolulu"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <p className="text-xs text-muted-foreground mt-1">Used to open the 5-minute check-in window and group today's classes.</p>
      </div>
      <div>
        <p className="font-semibold mb-1">Require pickup by level</p>
        <div className="flex flex-wrap gap-2">
          {cfg.levels.map((l) => <button key={l.id} type="button" data-testid={`pickup-level-${l.id}`} onClick={() => toggle("pickup_required_level_ids", l.id)}
            className={`px-3 py-1.5 rounded-full text-sm border ${cfg.pickup_required_level_ids.includes(l.id) ? "bg-teal text-white border-teal" : "bg-white"}`}>{l.name}</button>)}
        </div>
      </div>
      <div>
        <p className="font-semibold mb-1">Require pickup for specific classes</p>
        <div className="flex flex-wrap gap-2">
          {cfg.classes.map((c) => <button key={c.id} type="button" data-testid={`pickup-class-${c.id}`} onClick={() => toggle("pickup_required_class_ids", c.id)}
            className={`px-3 py-1.5 rounded-full text-sm border ${cfg.pickup_required_class_ids.includes(c.id) ? "bg-teal text-white border-teal" : "bg-white"}`}>{c.name} <span className="opacity-60">{c.day} {c.end_time}</span></button>)}
        </div>
      </div>
      <Button className="rounded-full bg-teal hover:bg-teal-dark" data-testid="pickup-settings-save" onClick={save}>Save settings</Button>
    </div>
  );
}
